<template>
  <ElFormItem
    :prop="name"
    :label="label"
    :double="doubleWidth"
    class="fi-date-cont"
  >
    <ElDatePicker
      v-model="value"
      v-bind="$attrs"
      :type="time ? 'datetime' : 'date'"
      :format="time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'"
      :value-format="time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'"
      unlink-panels
      :clearable="clearable"
    />

    <HelpIcon :help="help" />
  </ElFormItem>
</template>

<script lang="ts" setup>
/**
 * ### 日期选择组件
 * 提交字符串格式为 /^YYYY-MM-DD(?: HH:mm:ss)?$/
 * 传入未指定属性会透传到 ElDatePicker 组件中
 */
import { computed } from 'vue';
import { ElFormItem, ElDatePicker } from 'element-plus';
import dayjs from 'dayjs';
import { replace, split } from 'lodash-es';
import { useShareData } from '../../../core/SharedData';
import HelpIcon from '../../../common/HelpIcon.vue';

const props = withDefaults(
  defineProps<{
    /**
     * ### post参数名
     */
    name: string;
    /**
     * ### 表单项名
     */
    label: string;
    /**
     * ### 默认值
     */
    defaultValue?: string;
    /**
     * ### 是否显示时间选择器 */
    time?: boolean;
    /**
     * ### 组件是否双倍宽度
     */
    double?: boolean;
    /**
     * ### 帮助信息
     */
    help?: string;
    /**
     * ### 是否可清除
     */
    clearable?: boolean;
  }>(),
  {
    time: true, // 默认带时间
    clearable: true
  }
);

const model = useShareData('form', 'model');
// eslint-disable-next-line vue/no-setup-props-destructure
const name = props.name;
if (model[name] === undefined) {
  if (props.defaultValue == 'now')
    model[name] = dayjs().format(
      props.time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
    );
  // eslint-disable-next-line vue/no-setup-props-destructure
  else model[name] = props.defaultValue;
}

// 在form中注册
useShareData('form', 'items').$(name)(true);

const value = computed({
  get: () => {
    const v = model[name];
    if (!v) return v;
    const s = v.toString();
    if (v.includes('T')) {
      if (props.time) return replace(s, 'T', ' ');

      return split(s, 'T')[0];
    }
    return v;
  },
  set: (v: any) => {
    model[name] = v;
  }
});

// 双倍宽度
const doubleWidth = props.double ? '' : undefined;
</script>

<style lang="scss" scoped>
.fi-date-cont {
  width: auto;

  :deep(.el-input) {
    height: var(--fi-line-height) !important;
    line-height: calc(var(--fi-line-height) - 2px);

    .el-input__wrapper {
      height: var(--fi-line-height) !important;
      box-sizing: border-box;
      border: 1px solid var(--fi-border-color) !important;
      border-radius: var(--fi-border-radius);
      box-shadow: none;

      .el-input__inner {
        padding-left: 4px;
      }
    }

    .el-input__wrapper:hover {
      border-color: var(--fi-border-hover-color) !important;
    }

    &[aria-expanded='true'] .el-input__wrapper {
      border-color: var(--fi-border-hover-color) !important;
    }
  }

  .el-input + .el-icon {
    margin-left: 8px;
  }
}

.fi-date-cont[double] {
  grid-column-start: span 2;
}
</style>
